"use client";

import { useAuth } from "@clerk/nextjs";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import {
  clearPendingMarketplaceOnboardingRedirect,
  hasClerkPostAuthSearchParams,
  isSameMarketplaceRedirect,
  marketplaceRedirectPath,
  readPendingMarketplaceOnboardingRedirect,
  toAbsoluteAuthRedirectUrl,
} from "~/lib/marketplace/marketplace-onboarding-redirect";

function isClerkAuthFlowPath(pathname: string): boolean {
  return (
    pathname.startsWith("/sign-in") ||
    pathname.startsWith("/sign-up") ||
    pathname.includes("/factor-one") ||
    pathname.includes("/factor-two") ||
    pathname.includes("/sso-callback")
  );
}

/**
 * After Google OAuth, Clerk returns to the marketing page without modal state.
 * Resume onboarding when a pending hire URL was stored before sign-up.
 */
export function MarketplaceOnboardingRedirectRecovery() {
  const { isSignedIn, isLoaded } = useAuth();
  const router = useRouter();
  const pathname = usePathname() ?? "/";
  const redirected = useRef(false);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || redirected.current) return;
    if (isClerkAuthFlowPath(pathname)) return;

    const search = typeof window !== "undefined" ? window.location.search : "";
    const clerkPostAuth = hasClerkPostAuthSearchParams(search);
    const pending = readPendingMarketplaceOnboardingRedirect();

    if (!clerkPostAuth) {
      if (pending) clearPendingMarketplaceOnboardingRedirect();
      return;
    }

    if (!pending) return;

    const current = `${pathname}${search}`;
    if (isSameMarketplaceRedirect(current, pending)) {
      clearPendingMarketplaceOnboardingRedirect();
      return;
    }

    redirected.current = true;
    clearPendingMarketplaceOnboardingRedirect();

    const absolute = toAbsoluteAuthRedirectUrl(pending);
    if (absolute.startsWith("http")) {
      window.location.assign(absolute);
      return;
    }

    router.replace(marketplaceRedirectPath(pending));
  }, [isLoaded, isSignedIn, pathname, router]);

  return null;
}
